import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@retailsync/ui'
import { CheckIcon } from '@heroicons/react/24/solid'
import { useState } from 'react'
import { ImportSession, DataEntity } from '@retailsync/shared-types'
import { POS_TEMPLATES } from '../../data/templates'

interface FormatSelectionStepProps {
  session: ImportSession
  onUpdate: (updates: Partial<ImportSession>) => void
}

export function FormatSelectionStep({ session, onUpdate }: FormatSelectionStepProps) {
  const [loading] = useState(false)

  // Filter templates by selected entity
  const templates = session.entity
    ? POS_TEMPLATES.filter(t => t.entity === session.entity)
    : POS_TEMPLATES

  const handleTemplateSelect = (template: typeof POS_TEMPLATES[number]) => {
    const frontendTemplate = {
      id: template.id,
      name: template.name,
      format: template.format,
      description: template.description,
      entity: template.entity as DataEntity,
      defaultMappings: template.defaultMappings.map(m => ({
        sourceField: m.sourceField,
        targetField: m.targetField,
        transform: m.transform,
        required: m.required,
      })),
    }

    onUpdate({
      selectedTemplate: frontendTemplate,
      mappings: frontendTemplate.defaultMappings,
    })
  }

  const getFormatIcon = (format: string) => {
    switch (format) {
      case 'csv':
        return '📄'
      case 'excel':
        return '📊'
      default:
        return '📁'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading templates...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* File Info */}
      {session.uploadedFile && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <CheckIcon className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">File Detected</h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  File: <strong>{session.uploadedFile.name}</strong>
                </p>
                {session.rawData && session.rawData.length > 0 && (
                  <p className="mt-1">
                    {session.rawData.length} rows • {session.rawData[0] ? Object.keys(session.rawData[0]).length : 0} columns detected
                  </p>
                )}
                <p className="mt-1">
                  Choose the POS system that exported this data below.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Available Templates */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Available Templates</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <Card
              key={template.id}
              className={`cursor-pointer transition-all ${
                session.selectedTemplate?.id === template.id
                  ? 'ring-2 ring-blue-500 bg-blue-50'
                  : 'hover:shadow-md'
              }`}
              onClick={() => handleTemplateSelect(template)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">{getFormatIcon(template.format)}</span>
                    <CardTitle className="text-base">{template.name}</CardTitle>
                  </div>
                  {session.selectedTemplate?.id === template.id && (
                    <CheckIcon className="h-5 w-5 text-blue-600" />
                  )}
                </div>
                <CardDescription className="text-sm">
                  {template.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-xs text-gray-500">
                  Format: {template.format.toUpperCase()} • {template.defaultMappings.length} field mappings
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Selected Template Info */}
      {session.selectedTemplate && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start">
            <CheckIcon className="h-5 w-5 text-green-400 mt-0.5" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Template Selected</h3>
              <p className="mt-1 text-sm text-green-700">
                {session.selectedTemplate.name} — {session.selectedTemplate.defaultMappings?.length ?? 0} field mappings configured
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}